import axios, { AxiosError, AxiosInstance } from 'axios'
import { getSession, signOut } from 'next-auth/react'

export type useApiClientType = () => AxiosInstance
export type useApiClientResponseType = ReturnType<useApiClientType>

const SESSION_CACHE_MS = 30_000

type Session = Awaited<ReturnType<typeof getSession>>
let sessionCache: { session: Session; at: number } | null = null
let sessionPromise: Promise<Session> | null = null
let isSigningOut = false

export function getSessionForApi(): Promise<Session> {
  const now = Date.now()
  if (sessionCache && now - sessionCache.at < SESSION_CACHE_MS) {
    return Promise.resolve(sessionCache.session)
  }
  sessionPromise ??= getSession().then((session) => {
    sessionCache = { session, at: Date.now() }
    sessionPromise = null
    return session
  })
  return sessionPromise
}

export function clearSessionCache(): void {
  sessionCache = null
  sessionPromise = null
}

function handleUnauthorized(): void {
  clearSessionCache()
  if (isSigningOut) return
  isSigningOut = true
  void signOut({ callbackUrl: '/sign-in' }).finally(() => {
    isSigningOut = false
  })
}

export const ApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_SAVIOO_API_URL,
  })

  instance.interceptors.request.use(async (request) => {
    const session = await getSessionForApi()
    if (session?.error === 'RefreshAccessTokenError') {
      handleUnauthorized()
      return Promise.reject(new Error('Session refresh failed'))
    }
    if (session) {
      request.headers.Authorization = `Bearer ${session.accessToken}`
    }
    return request
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        handleUnauthorized()
      }
      return Promise.reject(error)
    },
  )

  return instance
}
